// Agent Board Summary — top-of-page rollup for the agent-board dashboard.
//
// CONVENTION: every agent-board widget's outermost container <div> carries
// `id="widget-${WidgetDefinition.id}"`. Widget 6 navigates between widgets
// by calling
//
//   document.getElementById('widget-<target>')?.scrollIntoView({ behavior: 'smooth' });
//
// New agent-board widgets must follow the same pattern so this rollup can
// link to them.
//
// Cache keys are shared with Widgets 1 (approvals) and 5 (eligibility) so
// SWR de-dupes the network round-trip — Widget 6 reuses the same fetched
// data, it does not issue a parallel request.
//
// ── CONTENT RATIFIED BY DIRECTOR 2026-05-03 ──
//
// The Lane 3 brief named `agent-board:runs:active` as one of Widget 6's
// SWR cache keys. There is no engine endpoint that backs that key — no
// list-active-runs route ships in seitiate@97e36ca. Director ruled (A):
// the eligibility-derived rollup is the canonical Widget 6 content.
// This widget therefore shows:
//
//   - Pending approvals       (Widget 1's source of truth)
//   - Eligible to dispatch    (Widget 5's eligibility summary)
//   - Approval required       (Widget 5's eligibility summary)
//   - Blocked                 (Widget 5's eligibility summary)
//
// The active-runs aggregate is acknowledged in body copy as unavailable;
// operators are pointed at Widget 2 for per-task introspection. If a
// future Lane adds a list-active-runs route, this widget should grow a
// fifth tile rather than swapping any existing one.

import useSWR from 'swr';
import { listAgentApprovalRequests } from '@/api/agentApprovalRequests';
import { listEligibleTasks } from '@/api/agentTasks';
import type {
  ApprovalListResponse,
  EligibilityResponse,
} from '@/api/agentBoardTypes';
import { EngineHttpError } from '@/lib/engineHttp';
import type { WidgetDefinition, WidgetProps } from '@/types/widget';

const APPROVALS_KEY = 'agent-board:approvals:pending';
const ELIGIBLE_KEY = 'agent-board:eligible';

function scrollToWidget(widgetId: string) {
  const el = document.getElementById(`widget-${widgetId}`);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function approvalsFetcher(): Promise<ApprovalListResponse> {
  return listAgentApprovalRequests({ status: 'pending', limit: 50 });
}

function eligibleFetcher(): Promise<EligibilityResponse> {
  return listEligibleTasks();
}

function AgentBoardSummaryWidget({ context: _context }: WidgetProps) {
  const approvals = useSWR<ApprovalListResponse>(APPROVALS_KEY, approvalsFetcher, {
    refreshInterval: 15_000,
    revalidateOnFocus: false,
  });
  const eligibility = useSWR<EligibilityResponse>(ELIGIBLE_KEY, eligibleFetcher, {
    revalidateOnFocus: false,
  });

  const pendingApprovals = approvals.data?.count ?? null;
  const approvalsForbidden =
    approvals.error instanceof EngineHttpError && approvals.error.status === 403;

  const eligibilityErrored = !!eligibility.error;
  const eligibleCount = eligibility.data?.summary.eligible_count ?? null;
  const approvalRequiredCount =
    eligibility.data?.summary.approval_required_count ?? null;
  const blockedCount = eligibility.data?.summary.blocked_count ?? null;

  return (
    <div id="widget-agent-board-summary" className="sp-widget">
      <div className="sp-widget-header">
        <span>Agent Board Summary</span>
        <span style={{ fontSize: 11, fontWeight: 400, letterSpacing: 0, color: 'var(--sp-text-dim)' }}>
          Click any count to jump to that surface
        </span>
      </div>
      <div className="sp-widget-body" style={{ padding: 12 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: 8,
          }}
        >
          <SummaryTile
            label="Pending approvals"
            count={approvalsForbidden ? '—' : pendingApprovals}
            color="var(--sp-warn)"
            onClick={() => scrollToWidget('owner-approval-queue')}
            note={approvalsForbidden ? '403 — you do not own this queue.' : undefined}
            disabled={approvalsForbidden}
          />
          <SummaryTile
            label="Eligible to dispatch"
            count={eligibilityErrored ? '—' : eligibleCount}
            color="var(--sp-ok)"
            onClick={() => scrollToWidget('eligible-task-dispatcher')}
          />
          <SummaryTile
            label="Approval required"
            count={eligibilityErrored ? '—' : approvalRequiredCount}
            color="var(--sp-warn)"
            onClick={() => scrollToWidget('eligible-task-dispatcher')}
          />
          <SummaryTile
            label="Blocked"
            count={eligibilityErrored ? '—' : blockedCount}
            color="var(--sp-text-dim)"
            onClick={() => scrollToWidget('eligible-task-dispatcher')}
          />
        </div>
        <div style={{ marginTop: 8, fontSize: 10, color: 'var(--sp-text-dim)' }}>
          Active agent runs aggregate is not yet exposed by the engine. Use Widget 2
          (Agent Run Inspector) for per-task introspection.
        </div>
      </div>
    </div>
  );
}

interface SummaryTileProps {
  label: string;
  count: number | string | null;
  color: string;
  note?: string;
  disabled?: boolean;
  onClick: () => void;
}

function SummaryTile({ label, count, color, note, disabled, onClick }: SummaryTileProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        textAlign: 'left',
        padding: 10,
        background: 'transparent',
        border: '1px solid var(--sp-border)',
        borderTop: `2px solid ${color}`,
        borderRadius: 3,
        color: 'var(--sp-text)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.65 : 1,
      }}
    >
      <div
        style={{
          fontSize: 9,
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          color: 'var(--sp-text-dim)',
          marginBottom: 2,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>
        {count === null ? '…' : count}
      </div>
      {note ? (
        <div style={{ fontSize: 10, color: 'var(--sp-text-muted)', marginTop: 2 }}>{note}</div>
      ) : null}
    </button>
  );
}

export const agentBoardSummaryWidget: WidgetDefinition = {
  id: 'agent-board-summary',
  name: 'Agent Board Summary',
  description: 'Top-of-page rollup. Click a count to jump to the relevant widget.',
  icon: 'gauge',
  category: 'agents',
  scope: 'both',
  defaultSize: { w: 12, h: 3 },
  minSize: { w: 6, h: 2 },
  component: AgentBoardSummaryWidget,
};
